import React, { useState } from 'react';
import { IconChevronDown as ChevronDown, IconChevronRight as ChevronRight, IconCircleDot as CircleDot, IconDoorEnter as DoorOpen, IconMinus as Minus, IconPentagon as Pentagon, IconPlus as Plus, IconShape as Shapes, IconMasksTheater as Theater, IconTrash as Trash2, IconTypography as Type } from '@tabler/icons-react';
import { seatKey, shapeKey } from './geometry';
import { iconById, shapeIconId } from './objectIcons';
import { zoneSeatKeys } from './sections';
import type { Plan, Selection, Zone } from './types';
import { useDialogs } from './dialogs';
import { cx, TextInput } from './ui';

type Props = {
    plan: Plan;
    selection: Selection;
    activeZone: number;
    onActiveZone: (zi: number) => void;
    onSelect: (sel: Selection, additive?: boolean) => void;
    onAddZone: () => void;
    onUpdateZone: (zi: number, patch: Partial<Zone>) => void;
    onDeleteZone: (zi: number) => void;
    className?: string;
};

const LayersPanel: React.FC<Props> = ({ plan, selection, activeZone, onActiveZone, onSelect, onAddZone, onUpdateZone, onDeleteZone, className }) => {
    const dialogs = useDialogs();
    const [collapsed, setCollapsed] = useState<Set<number>>(new Set());
    const shapes = [
        ...(plan.stages ?? []).map(s => ({
            key: shapeKey('stage', s.id),
            name: s.name || (s.variant === 'line' ? 'Line' : ''),
            icon: s.variant === 'text' ? Type
                : s.variant === 'table' ? CircleDot
                : s.variant === 'line' ? Minus
                : iconById(shapeIconId(s)) ?? (s.variant === 'object' || s.variant === 'ga' ? Shapes : s.type === 'polygon' ? Pentagon : Theater),
        })),
        ...(plan.entrances ?? []).map(s => ({ key: shapeKey('entrance', s.id), name: s.name, icon: DoorOpen })),
    ];

    const toggleCollapsed = (zi: number) =>
        setCollapsed(prev => {
            const next = new Set(prev);
            if (next.has(zi)) next.delete(zi);
            else next.add(zi);
            return next;
        });

    return (
        <aside className={cx('shrink-0 select-none flex flex-col bg-white dark:bg-nightSurface', className ?? 'w-60 border-r border-gray-200 dark:border-nightBorder')}>
            <div className="flex items-center justify-between px-4 h-10 border-b border-gray-200 dark:border-nightBorder">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-nightMuted">Sections</span>
                <button type="button" onClick={onAddZone} title="Add zone" className="h-6 w-6 flex items-center justify-center rounded text-gray-500 hover:bg-gray-100 dark:text-nightMuted dark:hover:bg-nightElevated">
                    <Plus className="h-3.5 w-3.5" />
                </button>
            </div>

            <div className="flex-1 overflow-y-auto">
                {plan.zones.map((zone, zi) => {
                    const active = zi === activeZone;
                    const open = !collapsed.has(zi);
                    const seatCount = zone.rows.reduce((n, r) => n + r.seats.length, 0);
                    return (
                        <div key={zi} className="border-b border-gray-100 dark:border-nightBorder/60">
                            <div
                                className={cx(
                                    'group flex items-center gap-1 pl-2 pr-2 h-9 cursor-pointer',
                                    active ? 'bg-primary99 dark:bg-nightAccent/10' : 'hover:bg-gray-50 dark:hover:bg-nightElevated',
                                )}
                                onClick={e => {
                                    onActiveZone(zi);
                                    if (zone.section) onSelect({ seats: new Set(zoneSeatKeys(zone)), shapes: new Set() }, e.shiftKey || e.metaKey || e.ctrlKey);
                                }}
                            >
                                <button type="button" onClick={e => { e.stopPropagation(); toggleCollapsed(zi); }} className="h-5 w-5 flex items-center justify-center text-gray-400">
                                    {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                                </button>
                                <span className={cx('h-1.5 w-1.5 rounded-full', active ? 'bg-primary40 dark:bg-nightAccent' : 'bg-transparent')} />
                                <span className="flex-1 truncate text-xs font-medium text-gray-800 dark:text-nightTextSecondary">{zone.name}</span>
                                <span className="text-[10px] tabular-nums text-gray-400">{seatCount}</span>
                                {plan.zones.length > 1 && (
                                    <button
                                        type="button"
                                        title="Delete zone"
                                        onClick={e => {
                                            e.stopPropagation();
                                            if (!seatCount) {
                                                onDeleteZone(zi);
                                                return;
                                            }
                                            dialogs.confirm({ title: 'Delete zone', message: `Delete "${zone.name}" and its ${seatCount} seats?`, confirmLabel: 'Delete', destructive: true }).then(ok => {
                                                if (ok) onDeleteZone(zi);
                                            });
                                        }}
                                        className="h-5 w-5 hidden group-hover:flex items-center justify-center rounded text-gray-400 hover:text-error40"
                                    >
                                        <Trash2 className="h-3 w-3" />
                                    </button>
                                )}
                            </div>

                            {active && (
                                <div className="grid grid-cols-[1fr_64px] gap-1 px-3 py-2 bg-gray-50/70 dark:bg-nightSecondary/40">
                                    <TextInput value={zone.name} placeholder="Zone name" onCommit={v => onUpdateZone(zi, { name: v || 'Zone' })} />
                                    <TextInput value={zone.code ?? ''} placeholder="Code" onCommit={v => onUpdateZone(zi, { code: v })} />
                                </div>
                            )}

                            {open && zone.rows.length > 0 && (
                                <ul className="pb-1">
                                    {zone.rows.map(row => {
                                        const keys = row.seats.map(seatKey);
                                        const full = keys.length > 0 && keys.every(k => selection.seats.has(k));
                                        const partial = !full && keys.some(k => selection.seats.has(k));
                                        return (
                                            <li
                                                key={row.uuid}
                                                onClick={e => {
                                                    onActiveZone(zi);
                                                    onSelect({ seats: new Set(keys), shapes: new Set() }, e.shiftKey || e.metaKey || e.ctrlKey);
                                                }}
                                                className={cx(
                                                    'flex items-center gap-2 pl-9 pr-3 h-7 text-xs cursor-pointer',
                                                    full ? 'bg-sky-50 text-sky-700 dark:bg-sky-500/10 dark:text-sky-300' : 'text-gray-600 hover:bg-gray-50 dark:text-nightMuted dark:hover:bg-nightElevated',
                                                )}
                                            >
                                                <span className={cx('h-1.5 w-1.5 rounded-full', full ? 'bg-sky-500' : partial ? 'bg-sky-300' : 'bg-gray-300 dark:bg-nightBorder')} />
                                                <span className="flex-1 truncate">{row.tableId ? 'Table' : 'Row'} {row.row_number || '(no label)'}</span>
                                                <span className="tabular-nums text-[10px] text-gray-400">{row.seats.length}</span>
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                            {open && !zone.rows.length && <p className="pl-9 pr-3 pb-2 text-[11px] text-gray-400">No rows yet</p>}
                        </div>
                    );
                })}

                <div className="flex items-center px-4 h-10 mt-2">
                    <span className="text-[11px] font-semibold uppercase tracking-wider text-gray-500 dark:text-nightMuted">Objects</span>
                </div>
                {shapes.length ? (
                    <ul>
                        {shapes.map(s => {
                            const Icon = s.icon;
                            const sel = selection.shapes.has(s.key);
                            return (
                                <li
                                    key={s.key}
                                    onClick={e => onSelect({ seats: new Set(), shapes: new Set([s.key]) }, e.shiftKey || e.metaKey || e.ctrlKey)}
                                    className={cx(
                                        'flex items-center gap-2 px-4 h-7 text-xs cursor-pointer',
                                        sel ? 'bg-sky-50 text-sky-700 dark:bg-sky-500/10 dark:text-sky-300' : 'text-gray-600 hover:bg-gray-50 dark:text-nightMuted dark:hover:bg-nightElevated',
                                    )}
                                >
                                    <Icon className="h-3.5 w-3.5 shrink-0" />
                                    <span className="truncate">{s.name || 'Untitled'}</span>
                                </li>
                            );
                        })}
                    </ul>
                ) : (
                    <p className="px-4 text-[11px] text-gray-400">Objects, tables and labels appear here</p>
                )}
            </div>
        </aside>
    );
};

export default LayersPanel;
